// Auth MFE entry point
import { createApp } from 'vue';
import { setupApp } from '@Src/app/setupApp';
import { createMountLifecycle } from '@Src/app/mountLifecycle';
import { AppContext } from 'host/enums';
import { setMfeContext } from 'host/mfeBridge';
import App from './App.vue';
import router from './router';
import { syncRouterToHost } from '@Router/syncRouterToHost';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import type { Company, Brand, Location } from '@Shared';

interface MountOptions {
    company: Company;
    brand?: Brand | null;
    location: Location | null;
}

// The re-entrancy guard lives in createMountLifecycle (DS-3181); this module only
// wires the real dependencies. See src/app/mountLifecycle.ts for the race rationale.
const lifecycle = createMountLifecycle<MountOptions>({
    setMfeContext: () => setMfeContext(AppContext.Auth),
    createAndSetupApp: async () => {
        const instance = createApp(App);
        await setupApp(instance);
        return instance;
    },
    routerIsReady: async () => {
        await router.isReady();
    },
    applyHostOptions: (options) => {
        if (options?.company) {
            const companyStore = useCompanyStore();
            companyStore.setCompany(options.company);
        }

        if (options?.brand) {
            const companyStore = useCompanyStore();
            companyStore.setBrand(options.brand);
        }

        if (options?.location) {
            const locationStore = useLocationStore();
            locationStore.setLocation(options.location);
        }
    },
    syncRouterToHost: () => syncRouterToHost(router),
});

export const mount = lifecycle.mount;
export const unmount = lifecycle.unmount;
